import Service from 'domains/models/service';
import { schemaServiceDetailListEventTypeItem } from '../event_type';
import { schemaServicePriceItem } from '../service_price';
import { CurrencyEnum } from '../service_price/enum';
import { schemaTaxRateCreateBody, schemaTaxRateItem } from '../tax_rate';
import {
  schemaServiceRestrictSettingCreateBody,
  schemaServiceRestrictSettingItem,
  schemaServiceRestrictSettingUpdateBody,
} from '../service_restrict_setting';
import { schemaServiceRestrictListItem } from '../service_restrict';
import { z } from 'zod';

export type ServiceCoverImageUpdateResult = z.infer<
  typeof schemaServiceCoverImageUpdateResult
>;
export const schemaServiceCoverImageUpdateResult = z.object({
  coverImage: z.string().nullable(),
});

export type ServiceCreateBody = z.infer<typeof schemaServiceCreateBody>;
export const schemaServiceCreateBody = z.object({
  service: z.object({
    teamId: z.number(),
    uid: z.string(),
    name: z.string(),
    isPaid: z.boolean(),
    description: z.string().nullable(),
    coverImage: z.string().nullable(),
    caption: z.string().nullable(),
    isShowPrice: z.boolean(),
  }),
  price: z
    .object({
      unitAmount: z.number().nullable(),
      currency: CurrencyEnum.nullable(),
      taxRateId: z.number().nullable(),
      taxRate: schemaTaxRateCreateBody.optional(),
    })
    .nullable(),
  restrictSetting: schemaServiceRestrictSettingCreateBody.nullable(),
});

export type ServiceUpdateBody = z.infer<typeof schemaServiceUpdateBody>;
export const schemaServiceUpdateBody = z.object({
  service: z.object({
    name: z.string().optional(),
    isPaid: z.boolean().optional(),
    isShowPrice: z.boolean().optional(),
    description: z.string().nullable().optional(),
    coverImage: z.string().nullable().optional(),
    caption: z.string().nullable().optional(),
  }),
  price: z.object({
    unitAmount: z.number().nullable(),
    currency: CurrencyEnum.nullable(),
    taxRateId: z.number().nullable(),
    taxRate: schemaTaxRateCreateBody.optional(),
  }),
  restrictSetting: schemaServiceRestrictSettingUpdateBody.nullable(),
});

export type ServiceCreateResult = z.infer<typeof schemaServiceCreateResult>;
export const schemaServiceCreateResult = z.object({
  id: z.number(),
});

export type ServiceUpdateResult = z.infer<typeof schemaServiceUpdateResult>;
export const schemaServiceUpdateResult = z.object({
  id: z.number(),
});

export type ServiceItem = z.infer<typeof schemaServiceItem>;
export const schemaServiceItem = z.object({
  id: z.number().nullable(),
  uid: z.string(),
  teamId: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  coverImage: z.string().nullable(),
  caption: z.string().nullable(),
  isPaid: z.boolean(),
  isShowPrice: z.boolean(),
});

export type ServiceListItem = z.infer<typeof schemaServiceListItem>;
export const schemaServiceListItem = z.object({
  id: z.number().nullable(),
  uid: z.string(),
  teamId: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  coverImage: z.string().nullable(),
  caption: z.string().nullable(),
  isPaid: z.boolean(),
  isShowPrice: z.boolean(),
});

export type ServiceDetailListItem = z.infer<typeof schemaServiceDetailListItem>;
export const schemaServiceDetailListItem = z.object({
  ...schemaServiceListItem.shape,
  price: schemaServicePriceItem.nullable(),
  taxRate: schemaTaxRateItem.nullable(),
  eventTypes: z.array(schemaServiceDetailListEventTypeItem),
});

export type ServiceDetailItem = z.infer<typeof schemaServiceDetailItem>;
export const schemaServiceDetailItem = z.object({
  ...schemaServiceListItem.shape,
  price: z.any(),
  restrictSetting: schemaServiceRestrictSettingItem.nullable(),
  restricts: z.array(schemaServiceRestrictListItem),
});

export const toItem = (service: Service): ServiceItem => {
  return {
    id: service.id,
    uid: service.uid,
    teamId: service.teamId,
    name: service.name,
    description: service.description,
    coverImage: service.coverImage,
    caption: service.caption,
    isPaid: service.isPaid,
    isShowPrice: service.isShowPrice,
  };
};

export const toListItem = (service: Service): ServiceListItem => {
  return {
    id: service.id,
    uid: service.uid,
    teamId: service.teamId,
    name: service.name,
    description: service.description,
    coverImage: service.coverImage,
    caption: service.caption,
    isPaid: service.isPaid,
    isShowPrice: service.isShowPrice,
  };
};
